import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { MuiTextField } from '../../../../../components/MuiComponents/FormInputs';
import Button from '../../../../../components/Button';
import Typography from '../../../../../components/Typography';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Divider } from '@mui/material';
import ErrorBanner from '../../../../../components/MuiComponents/MuiTextFieldErrorBanner';
import { uploadFile } from '../../../../../api/mutations/misc';
import {
  useGenPTTestSteps,
  useGenPTSessionDetails,
  useValidateAPICollection,
} from '../../../../../hooks/siembagpt';
import GreenCheck from '../../../../../components/SvgIcons/GreenCheck';
import { mergeSessionApiConfigForActivitiesViewSetup } from '../utils/genptActivitiesSessionCollection';

/**
 * How many distinct collection items this payload describes (for picking the best source to render).
 * `widget.data` often has only a legacy `file_key` / single URL while `metadata.api_collection_config` has the full arrays.
 */
function scoreCollectionPayloadForDisplay(data) {
  if (data == null || typeof data !== 'object') return 0;
  let files = 0;
  if (Array.isArray(data.api_files) && data.api_files.length > 0) files = data.api_files.length;
  else if (Array.isArray(data.files) && data.files.length > 0) files = data.files.length;
  else if (data.file_key || data.api_file_key || data.collection_s3_filename) files = 1;

  let urls = 0;
  if (Array.isArray(data.api_collection_urls) && data.api_collection_urls.length > 0) {
    urls = data.api_collection_urls.map((u) => String(u).trim()).filter(Boolean).length;
  } else if (
    (typeof data.api_collection_url === 'string' && data.api_collection_url.trim()) ||
    (typeof data.apiCollectionUrl === 'string' && data.apiCollectionUrl.trim()) ||
    (typeof data.collection_url === 'string' && data.collection_url.trim())
  ) {
    urls = 1;
  }
  return files + urls;
}

/** True when the object actually describes files/URLs (not `{}` after reload). */
function apiCollectionPayloadIsMeaningful(data) {
  if (data == null || typeof data !== 'object') return false;
  if (Array.isArray(data.api_files) && data.api_files.length > 0) return true;
  if (Array.isArray(data.files) && data.files.length > 0) return true;
  if (Array.isArray(data.api_collection_urls) && data.api_collection_urls.length > 0) return true;
  if (typeof data.collection_url === 'string' && data.collection_url.trim() !== '') return true;
  if (typeof data.collection_file === 'string' && data.collection_file.trim() !== '') return true;
  if (data.file_key || data.api_file_key || data.collection_s3_filename) return true;
  const single = data.api_collection_url ?? data.apiCollectionUrl;
  if (typeof single === 'string' && single.trim() !== '') return true;
  return false;
}

/**
 * GET session merges collection across `api_config`, `test_config`, schedule, root — same as Activities.
 * Normalize to the `api_config` shape the widget expects for prefill / display.
 */
function mergedSessionCollectionToApiConfig(merged) {
  if (!merged || typeof merged !== 'object') return null;
  const files = merged.api_files?.length
    ? merged.api_files
    : merged.files?.length
      ? merged.files
      : null;
  let urls = merged.api_collection_urls?.length ? merged.api_collection_urls : null;
  if (!urls?.length && typeof merged.collection_url === 'string' && merged.collection_url.trim() !== '') {
    urls = [merged.collection_url.trim()];
  }
  /** Legacy rows: never return both modalities — file upload replaces URL collection. */
  if (files?.length && urls?.length) urls = null;
  const fk = merged.file_key || merged.collection_s3_filename;
  if (!files?.length && !urls?.length && fk) {
    const fileName =
      merged.collection_original_filename || String(fk).split('/').pop() || 'file';
    return {
      api_files: [{ file_key: fk, file_name: fileName }],
      ...(merged.schema_endpoint_url && { schema_endpoint_url: merged.schema_endpoint_url }),
      ...(merged.format && { format: merged.format }),
      ...(merged.collection_format && { collection_format: merged.collection_format }),
    };
  }
  if (!files?.length && !urls?.length) return null;
  return {
    ...(files?.length && { api_files: files }),
    ...(urls?.length && { api_collection_urls: urls }),
    ...(merged.schema_endpoint_url && { schema_endpoint_url: merged.schema_endpoint_url }),
    ...(merged.format && { format: merged.format }),
    ...(merged.collection_format && { collection_format: merged.collection_format }),
  };
}

/**
 * Read-only expand only renders `api_collection_urls`, `api_files`/`files`, `schema_endpoint_url`.
 * Persisted `widget.data` / metadata often use singular `api_collection_url`, `collection_url`, or `file_key` only —
 * those count as “meaningful” for gating but would otherwise show an empty body.
 */
function normalizeCollectionDisplayForExpand(data) {
  if (!data || typeof data !== 'object') return {};
  const out = { ...data };

  const trimmedUrls = Array.isArray(out.api_collection_urls)
    ? out.api_collection_urls.map((u) => String(u).trim()).filter(Boolean)
    : [];
  if (trimmedUrls.length === 0) {
    const single =
      (typeof out.api_collection_url === 'string' && out.api_collection_url.trim()) ||
      (typeof out.apiCollectionUrl === 'string' && out.apiCollectionUrl.trim()) ||
      (typeof out.collection_url === 'string' && out.collection_url.trim()) ||
      '';
    if (single) out.api_collection_urls = [single];
  } else {
    out.api_collection_urls = trimmedUrls;
  }

  const hasApiFiles = Array.isArray(out.api_files) && out.api_files.length > 0;
  const hasLegacyFiles = Array.isArray(out.files) && out.files.length > 0;
  if (!hasApiFiles && hasLegacyFiles) {
    out.api_files = out.files.map((f) => ({
      file_key: f.file_key ?? f.fileKey,
      file_name:
        f.file_name ??
        f.fileName ??
        (String(f.file_key || f.fileKey || '').split('/').pop() || 'file'),
    }));
  }
  const fk = out.file_key || out.api_file_key;
  if (!(Array.isArray(out.api_files) && out.api_files.length > 0) && fk) {
    const name =
      out.collection_original_filename || String(fk).split('/').pop() || 'file';
    out.api_files = [{ file_key: fk, file_name: name }];
  }

  const urlCount = Array.isArray(out.api_collection_urls)
    ? out.api_collection_urls.map((u) => String(u).trim()).filter(Boolean).length
    : 0;
  const fileCount = Array.isArray(out.api_files) ? out.api_files.length : 0;
  if (urlCount > 0 && fileCount > 0) {
    delete out.api_collection_urls;
    delete out.api_collection_url;
    delete out.apiCollectionUrl;
    delete out.collection_url;
  }

  return out;
}

function resolveApiConfigForWidgetPrefill(latestSession, mergedCollection, sessionDetails) {
  const fromMsg = latestSession?.api_config;
  if (fromMsg && typeof fromMsg === 'object') {
    if (fromMsg.api_files?.length > 0 || fromMsg.api_collection_urls?.length > 0) {
      const normalizedMsg = mergedSessionCollectionToApiConfig(fromMsg);
      if (normalizedMsg) return normalizedMsg;
    }
  }
  const fromMerged = mergedSessionCollectionToApiConfig(mergedCollection);
  if (fromMerged) return fromMerged;
  if (fromMsg && typeof fromMsg === 'object') return fromMsg;
  return sessionDetails?.api_config ?? null;
}

export default function APICollectionSourceWidget({
  msg = null,
  targetURL = null,
  chatId = null,
  sessionDetails = null,
  /**
   * GenPT session id for GET `/genpt/sessions/:id`. Subscribes via React Query with the same key as
   * `GenPTChatPage` / Activities — no extra HTTP request when the cache is already warm.
   * Omit in schedule modal (`embeddedInScheduleModal`).
   */
  sessionId = null,
  /** ROAR-4364: Schedule GenPT modal — skip `/genpt/test/steps` and return normalized api_config to parent. */
  embeddedInScheduleModal = false,
  onScheduleCollectionComplete = null,
  /** When parent renders the section title + card (schedule modal), hide duplicate in-widget heading. */
  suppressSectionChrome = false,
  /** Schedule GenPT step 1: label for the primary submit action (e.g. SETUP CONFIG). */
  scheduleModalPrimaryCtaLabel = null,
}) {
  const isReadOnly = msg?.status === 'completed';
  const submittedDataProp = msg?.widget?.data || (msg?.metadata?.asset?.api_file_key || msg?.metadata?.asset?.api_collection_url ? {
    file_key: msg?.metadata?.asset?.api_file_key,
    api_collection_url: msg?.metadata?.asset?.api_collection_url,
  } : null);
  const genPTTestSteps = useGenPTTestSteps();
  const validateAPICollection = useValidateAPICollection();

  const sessionIdForQuery =
    sessionId != null && String(sessionId).trim() !== '' ? String(sessionId).trim() : '';
  const shouldSubscribeSessionQuery = !embeddedInScheduleModal && Boolean(sessionIdForQuery);
  const { data: sessionDetailsQueryPayload } = useGenPTSessionDetails({
    sessionId: sessionIdForQuery,
    enabled: shouldSubscribeSessionQuery,
    refetchInterval: (data) => {
      if (data?.session?.status === 'inprogress') return 7200;
      return false;
    },
  });
  const effectiveSessionDetails =
    sessionDetailsQueryPayload?.session ?? sessionDetails ?? null;

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    setError,
    clearErrors,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      collection_file: '',
      collection_url: '',
    },
  });

  const [submittedData, setSubmittedData] = useState(submittedDataProp || null);
  const [formVisible, setFormVisible] = useState(!isReadOnly);
  const [expanded, setExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [s3FileKey, setS3FileKey] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [isPrefilledFromSession, setIsPrefilledFromSession] = useState(false);
  const [isUrlPrefilledFromSession, setIsUrlPrefilledFromSession] = useState(false);
  const [isVerifyingUrl, setIsVerifyingUrl] = useState(false);
  const [isVerifyingFile, setIsVerifyingFile] = useState(false);
  const [isUrlVerified, setIsUrlVerified] = useState(false);
  const [isFileVerified, setIsFileVerified] = useState(false);
  const [urlFormat, setUrlFormat] = useState(null); // Store format from URL verification response (e.g. GraphQL)
  const [fileFormat, setFileFormat] = useState(null); // Store format from file verification response (e.g. GraphQL)
  const [additionalUrls, setAdditionalUrls] = useState([]);
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [schemaEndpointPath, setSchemaEndpointPath] = useState(''); // Path only (e.g. graphql, api). User cannot type "/"
  const [schemaEndpointUrlError, setSchemaEndpointUrlError] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState({}); // Track upload progress by file ID
  const hasInitializedFromSession = useRef(false);
  const hasInitializedFromProp = useRef(false);

  const HOST_MISMATCH_DISPLAY_MESSAGE = 'Host mismatch: The domain in your collection URL does not match the domain entered. Please check and try again.';
  const MAX_ADDITIONAL_SOURCES = 4; // 1 main + 4 additional = 5 total sources max

  const isHostMismatchError = (data) => {
    if (!data) return false;
    const msg = data.message;
    const code = data.code;
    return (msg && String(msg).toLowerCase().includes('host mismatch')) || code === 'E11';
  };

  const buildDisplayFromValidationResults = (results) => {
    if (!results?.length) return null;
    const isUrl = (label) => /^https?:\/\//i.test(label);
    const urls = results.filter((r) => isUrl(r.label)).map((r) => r.label);
    const files = results.filter((r) => !isUrl(r.label)).map((r) => ({
      file_name: r.label,
      format: r.format,
    }));
    const data = {};
    if (urls.length > 0) data.api_collection_urls = urls;
    if (files.length > 0) data.api_files = files;
    if (urls.length > 0 && files.length > 0) delete data.api_collection_urls;
    return Object.keys(data).length > 0 ? data : null;
  };

  const buildDisplayFromApiConfig = (apiConfig) => {
    const hasFiles = apiConfig.api_files?.length > 0;
    const hasUrls = apiConfig.api_collection_urls?.length > 0;
    if (!hasFiles && !hasUrls) return null;
    if (hasFiles && hasUrls) {
      return {
        api_files: apiConfig.api_files,
        ...(apiConfig.schema_endpoint_url && { schema_endpoint_url: apiConfig.schema_endpoint_url }),
      };
    }
    return {
      ...(hasFiles && { api_files: apiConfig.api_files }),
      ...(hasUrls && { api_collection_urls: apiConfig.api_collection_urls }),
      ...(apiConfig.schema_endpoint_url && { schema_endpoint_url: apiConfig.schema_endpoint_url }),
    };
  };

  const mergedSessionCollection = useMemo(
    () => mergeSessionApiConfigForActivitiesViewSetup(effectiveSessionDetails),
    [effectiveSessionDetails]
  );

  const displayData = useMemo(() => {
    const fromMetaConfig =
      msg?.metadata?.api_collection_config ?? msg?.metadata?.apiCollectionConfig ?? null;
    const validationResults = msg?.metadata?.api_collection_validation_results;
    const fromValidation = buildDisplayFromValidationResults(validationResults);
    const normalizedSessionApi = mergedSessionCollectionToApiConfig(mergedSessionCollection);
    const fromSession = normalizedSessionApi
      ? buildDisplayFromApiConfig(normalizedSessionApi)
      : null;

    /**
     * Prefer the payload that lists the most files/URLs. `submittedData` / `widget.data` is often a sparse legacy
     * shape (single `file_key`) while `api_collection_config` or session merge has the full `api_files` / `api_collection_urls`.
     * On equal richness, earlier entries win (in-session save beats stale duplicate on message).
     */
    const candidates = [
      submittedData,
      fromMetaConfig,
      fromValidation,
      fromSession,
      submittedDataProp,
    ];

    let raw = {};
    let bestScore = -1;
    let bestIdx = Infinity;
    candidates.forEach((c, idx) => {
      if (!apiCollectionPayloadIsMeaningful(c)) return;
      const s = scoreCollectionPayloadForDisplay(c);
      if (s > bestScore || (s === bestScore && idx < bestIdx)) {
        bestScore = s;
        bestIdx = idx;
        raw = c;
      }
    });

    return normalizeCollectionDisplayForExpand(raw);
  }, [
    submittedData,
    submittedDataProp,
    msg?.metadata?.api_collection_config,
    msg?.metadata?.apiCollectionConfig,
    msg?.metadata?.api_collection_validation_results,
    mergedSessionCollection,
  ]);

  const collectionFile = watch('collection_file');
  const collectionUrl = watch('collection_url');

  // Prefer targetURL from parent (GenPTChatPanel passes it from URL param / socket / localStorage), then metadata
  const effectiveTargetUrl =
    targetURL?.trim() ||
    msg?.metadata?.asset?.url?.trim() ||
    msg?.metadata?.target_url?.trim() ||
    msg?.metadata?.targetURL?.trim() ||
    msg?.metadata?.latest_session?.target_url?.trim() ||
    '';

  const hasFile = s3FileKey !== null;
  const hasUrl = collectionUrl && collectionUrl.trim() !== '';
  const exactlyOneFilled = (hasFile && !hasUrl) || (!hasFile && hasUrl);
  const urlHasError = Boolean(errors?.collection_url);

  // When GraphQL, schema endpoint path is required; disable Confirm until it is filled
  const schemaEndpointRequired = (hasFile && fileFormat === 'GraphQL') || (hasUrl && urlFormat === 'GraphQL');
  const schemaEndpointMissing = schemaEndpointRequired && !(schemaEndpointPath?.trim());

  // Confirm is disabled until verification has been done and succeeded for the current source (URL or file)
  const verificationMissing = (hasUrl && !isUrlVerified) || (hasFile && !isFileVerified);
  const isSubmitDisabled = !exactlyOneFilled || isSaving || isUploading || (hasUrl && urlHasError) || schemaEndpointMissing || verificationMissing;
  
  const shouldShowUploadedFile = uploadedFileName && s3FileKey && isPrefilledFromSession;
  const shouldShowPrefilledNote = (isUrlPrefilledFromSession && collectionUrl && collectionUrl.trim() !== '') || 
                                   (isPrefilledFromSession && uploadedFileName && s3FileKey);

  useEffect(() => {
    const latestSession = msg?.metadata?.latest_session;
    /** Prefer websocket `latest_session.api_config`; else same merged session collection as Activities. */
    const apiConfig = resolveApiConfigForWidgetPrefill(
      latestSession,
      mergedSessionCollection,
      effectiveSessionDetails
    );
    const apiConfigKey = apiConfig ? JSON.stringify({
      api_files: apiConfig.api_files,
      api_collection_urls: apiConfig.api_collection_urls,
      schema_endpoint_url: apiConfig.schema_endpoint_url,
      format: apiConfig.format || apiConfig.collection_format,
    }) : null;

    const setSchemaPathFromFullUrl = (fullUrl, origin) => {
      if (!fullUrl?.trim() || !origin) return;
      try {
        const url = new URL(fullUrl.trim());
        if (url.origin !== origin) return;
        const pathname = url.pathname || '';
        const path = pathname.replace(/^\/+/, '').split('/')[0] || ''; // first segment only (no slash allowed in field)
        if (path) setSchemaEndpointPath(path);
      } catch (_) {}
    };
    const parseUrlOrigin = (raw) => {
      const u = String(raw || '').trim();
      if (!u) return '';
      try {
        const withScheme = /^https?:\/\//i.test(u) ? u : `https://${u}`;
        return new URL(withScheme).origin;
      } catch {
        return '';
      }
    };
    const targetOrigin = parseUrlOrigin(effectiveTargetUrl);

    if (apiConfig && !isReadOnly && formVisible) {
      const prevKey = hasInitializedFromSession.current;
      if (prevKey !== apiConfigKey) {
        const inferGraphQLFromName = (nameOrKey) => {
          const s = String(nameOrKey || '').toLowerCase();
          if (s.endsWith('.graphql') || s.endsWith('.gql')) return 'GraphQL';
          return null;
        };
        const sessionFormat = apiConfig.format || apiConfig.collection_format || null;

        // File branch first: UI allows only one modality; if the API still returns both (stale merge),
        // prefer files so “switched URL → file” edits reopen correctly.
        if (apiConfig.api_files?.length > 0) {
          setValue('collection_url', '', { shouldValidate: false });
          setValue('collection_file', '', { shouldValidate: false });
          setIsUrlPrefilledFromSession(false);

          const files = apiConfig.api_files;
          const firstFile = files[0];
          const fileKey = firstFile?.file_key || null;
          const fileName = firstFile?.file_name || null;

          if (fileKey) {
            setS3FileKey(fileKey);
            setIsPrefilledFromSession(true);
          }
          if (fileName) setUploadedFileName(fileName);

          setIsFileVerified(true);
          const fileInferredFormat =
            sessionFormat ||
            (!sessionFormat && apiConfig.schema_endpoint_url ? 'GraphQL' : null) ||
            inferGraphQLFromName(fileName || fileKey);
          setFileFormat(fileInferredFormat);
          setSchemaPathFromFullUrl(apiConfig.schema_endpoint_url, targetOrigin);

          if (files.length > 1) {
            setAdditionalFiles(
              files.slice(1).map((f, i) => ({
                id: Date.now() + i,
                file: null,
                s3FileKey: f.file_key || null,
                fileName: f.file_name || f.file_key?.split('/').pop() || null,
              }))
            );
          }
        } else if (apiConfig.api_collection_urls?.length > 0) {
          const urls = apiConfig.api_collection_urls;
          const mainUrl = urls[0] || '';
          setValue('collection_url', mainUrl, { shouldValidate: false });
          setValue('collection_file', '', { shouldValidate: false });
          setS3FileKey(null);
          setUploadedFileName(null);
          setIsPrefilledFromSession(false);
          setIsUrlPrefilledFromSession(true);
          setIsUrlVerified(true);
          const urlInferred =
            sessionFormat ||
            (!sessionFormat && apiConfig.schema_endpoint_url ? 'GraphQL' : null);
          setUrlFormat(urlInferred);
          const collectionOrigin = parseUrlOrigin(mainUrl) || targetOrigin;
          setSchemaPathFromFullUrl(apiConfig.schema_endpoint_url, collectionOrigin);

          if (urls.length > 1) {
            setAdditionalUrls(
              urls.slice(1).map((u, i) => ({
                id: Date.now() + i,
                url: u || '',
              }))
            );
          }
        }
        hasInitializedFromSession.current = apiConfigKey;
      }
    } else if (!apiConfig) {
      hasInitializedFromSession.current = false;
      setIsUrlPrefilledFromSession(false);
      setIsPrefilledFromSession(false);
    }
  }, [
    msg?.metadata?.latest_session,
    mergedSessionCollection,
    effectiveSessionDetails,
    isReadOnly,
    formVisible,
    setValue,
    submittedDataProp,
    effectiveTargetUrl,
  ]);

  useEffect(() => {
    if (isReadOnly && submittedDataProp) {
      const propKey = JSON.stringify({
        collection_file: submittedDataProp?.collection_file,
        collection_url: submittedDataProp?.collection_url
      });
      const prevKey = hasInitializedFromProp.current;
      
      if (prevKey !== propKey) {
        setValue('collection_file', submittedDataProp?.collection_file || '', { shouldValidate: false });
        setValue('collection_url', submittedDataProp?.collection_url || '', { shouldValidate: false });
        hasInitializedFromProp.current = propKey;
      }
    } else if (!submittedDataProp) {
      hasInitializedFromProp.current = false;
    }
  }, [submittedDataProp, isReadOnly, setValue]);

  // Re-validate path format only when path or format changes (e.g. after SOAP verification, clear format error).
  // Do NOT set "Path is required" here — that would show the error as soon as the field appears after GraphQL verify.
  // "Path is required" is enforced only on submit and when user changes the field (handleSchemaEndpointPathChange).
  useEffect(() => {
    setSchemaEndpointUrlError(getSchemaEndpointPathError(schemaEndpointPath, false));
  }, [fileFormat, urlFormat, hasFile, hasUrl, schemaEndpointPath]);

  // When user removes the file and URL is empty, clear URL error so the field is not shown red (disabled Confirm is enough).
  useEffect(() => {
    if (!hasFile && !collectionUrl?.trim() && errors?.collection_url) {
      clearErrors('collection_url');
    }
  }, [hasFile, collectionUrl, errors?.collection_url, clearErrors]);

  const getFileExtension = (fileName) => {
    const ext = fileName.split('.').pop()?.toUpperCase();
    if (ext === 'GRAPHQL' || ext === 'GQL') return 'GQL';
    return ext || 'FILE';
  };

  const getBadgeTextSizeClass = (fileName) => {
    const label = getFileExtension(fileName);
    const len = (label || '').length;
    if (len <= 4) return 'text-xs';
    if (len <= 6) return 'text-[10px]';
    return 'text-[8px]';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const parseUrlOrigin = (raw) => {
    const u = String(raw || '').trim();
    if (!u) return '';
    try {
      const withScheme = /^https?:\/\//i.test(u) ? u : `https://${u}`;
      return new URL(withScheme).origin;
    } catch {
      return '';
    }
  };

  const getTargetUrlOrigin = () => parseUrlOrigin(effectiveTargetUrl);

  /**
   * GraphQL schema base: when the user chose a collection URL, its scheme/host (e.g. http) must drive
   * the read-only base + built schema_endpoint_url — not only the asset `targetURL` from the parent.
   * Schedule GenPT passes https asset URLs while QA uses http collection URLs (ROAR-4446).
   * File-only: prefer validate echo / latest_session target before normalized parent URL.
   */
  const getSchemaEndpointBaseOrigin = () => {
    const col = String(collectionUrl || '').trim();
    if (col) {
      const o = parseUrlOrigin(col);
      if (o) return o;
    }
    const inputTarget = String(
      msg?.metadata?.input_target_url ?? msg?.metadata?.inputTargetUrl ?? ''
    ).trim();
    if (inputTarget && /^https?:\/\//i.test(inputTarget)) {
      const o = parseUrlOrigin(inputTarget);
      if (o) return o;
    }
    const lsTarget = String(
      msg?.metadata?.latest_session?.target_url ?? msg?.metadata?.latest_session?.targetURL ?? ''
    ).trim();
    if (lsTarget && /^https?:\/\//i.test(lsTarget)) {
      const o = parseUrlOrigin(lsTarget);
      if (o) return o;
    }
    return getTargetUrlOrigin();
  };

  const getSchemaEndpointPathError = (path, requireForGraphQLOnly = false) => {
    const trimmed = path?.trim?.();
    if (trimmed?.includes('/')) return 'Slash (/) is not allowed. Enter a single path segment (e.g. graphql, api)';
    if (requireForGraphQLOnly && !trimmed) return 'Path is required (e.g. graphql, api)';
    return null;
  };

  const handleSchemaEndpointPathChange = (e) => {
    const raw = e.target.value;
    const value = raw.replace(/\//g, ''); // User is not allowed to add "/" in the field
    setSchemaEndpointPath(value);
    const requirePath = (hasFile && fileFormat === 'GraphQL') || (hasUrl && urlFormat === 'GraphQL');
    setSchemaEndpointUrlError(getSchemaEndpointPathError(value, requirePath));
    if (serverError) setServerError(null);
  };

  const buildSchemaEndpointFullUrl = () => {
    const origin = getSchemaEndpointBaseOrigin();
    const path = schemaEndpointPath?.trim?.() || '';
    if (!origin || !path) return '';
    const base = origin.endsWith('/') ? origin.slice(0, -1) : origin;
    const pathPart = path.startsWith('/') ? path : `/${path}`;
    return `${base}${pathPart}`;
  };

  const handleFileSelect = async (file, fieldOnChange) => {
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const allowedExtensions = ['.json', '.xml', '.yaml', '.yml', '.graphql', '.gql', '.wsdl'];
    const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (isValidExtension) {
      setIsUploading(true);
      setError('collection_file', null);
      setError('collection_url', null); // Clear URL error when user switches to file
      setServerError(null); // Clear URL/verification error when user switches to file

      try {
        const uploadedKey = await uploadFile({
          file: file,
          category: 'tmp',
          type: file.type || 'application/json',
        });
        
        setS3FileKey(uploadedKey);
        setUploadedFileName(file.name);
        setIsPrefilledFromSession(false); // Mark as newly uploaded, not prefilled
        setIsUrlPrefilledFromSession(false); // Reset URL prefilled flag when file is uploaded
                            setIsFileVerified(false); // Reset verification when new file is uploaded
                            setFileFormat(null); // Reset provider
                            setAdditionalFiles([]); // Clear additional files
                            setSchemaEndpointPath(''); setSchemaEndpointUrlError(null); // Clear schema endpoint URL
        fieldOnChange(file);
        setValue('collection_url', '', { shouldValidate: false }); // Don't validate empty URL (avoids red border when switching to file)
        setError('collection_url', null); // Ensure URL field error is cleared after clearing value
        setIsUploading(false);
      } catch (error) {
        setIsUploading(false);
        setS3FileKey(null);
        setIsPrefilledFromSession(false);
        setIsUrlPrefilledFromSession(false);
        fieldOnChange(null);
        setError('collection_file', {
          type: 'manual',
          message: error?.response?.data?.message || 'Failed to upload file. Please try again.'
        });
      }
    } else {
      fieldOnChange(null);
      setS3FileKey(null);
      setIsPrefilledFromSession(false);
      setIsUrlPrefilledFromSession(false);
      setError('collection_file', {
        type: 'manual',
        message: 'Only JSON, XML/WSDL, YAML, and GraphQL (.graphql/.gql) files are allowed'
      });
    }
  };


  const handleVerifyUrl = async () => {
    const url = collectionUrl?.trim();
    if (!url) return;
    
    setIsVerifyingUrl(true);
    try {
      const response = await validateAPICollection.mutateAsync({
        api_collection_url: url,
        ...(effectiveTargetUrl?.trim() && { target_url: effectiveTargetUrl.trim() }),
      });
      
      if (response?.success) {
        setIsUrlVerified(true);
        setUrlFormat(response?.format || null);
        setServerError(null);
      } else {
        setIsUrlVerified(false);
        setUrlFormat(null);
        setServerError(isHostMismatchError(response) ? HOST_MISMATCH_DISPLAY_MESSAGE : (response?.message || 'Verification failed'));
      }
    } catch (error) {
      setIsUrlVerified(false);
      setUrlFormat(null);
      setAdditionalUrls([]);
      setSchemaEndpointPath(''); setSchemaEndpointUrlError(null);
      const errData = error?.response?.data;
      const errorMessage = isHostMismatchError(errData) ? HOST_MISMATCH_DISPLAY_MESSAGE : (errData?.message || error?.message || 'URL verification failed');
      setServerError(errorMessage);
    } finally {
      setIsVerifyingUrl(false);
    }
  };

  const handleVerifyFile = async () => {
    // Backend expects an uploaded S3 key (string), not a File object
    if (!s3FileKey) return;
    
    setIsVerifyingFile(true);
    try {
      const response = await validateAPICollection.mutateAsync({
        file_key: s3FileKey,
        ...(effectiveTargetUrl?.trim() && { target_url: effectiveTargetUrl.trim() }),
      });
      
      if (response?.success) {
        setIsFileVerified(true);
        setFileFormat(response?.format || null);
        setServerError(null);
      } else {
        setIsFileVerified(false);
        setFileFormat(null);
        setServerError(isHostMismatchError(response) ? HOST_MISMATCH_DISPLAY_MESSAGE : (response?.message || 'Verification failed'));
      }
    } catch (error) {
      setIsFileVerified(false);
      setFileFormat(null);
      setAdditionalFiles([]);
      setSchemaEndpointPath(''); setSchemaEndpointUrlError(null);
      const errData = error?.response?.data;
      const errorMessage = isHostMismatchError(errData) ? HOST_MISMATCH_DISPLAY_MESSAGE : (errData?.message || error?.message || 'File verification failed');
      setServerError(errorMessage);
    } finally {
      setIsVerifyingFile(false);
    }
  };

  const handleAddMoreUrl = () => {
    if (additionalUrls.length >= MAX_ADDITIONAL_SOURCES) return;
    setAdditionalUrls([...additionalUrls, { id: Date.now(), url: '' }]);
  };

  const handleRemoveUrl = (id) => {
    setAdditionalUrls(additionalUrls.filter(item => item.id !== id));
  };

  const handleUpdateAdditionalUrl = (id, field, value) => {
    setAdditionalUrls(additionalUrls.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleAddMoreFile = () => {
    if (additionalFiles.length >= MAX_ADDITIONAL_SOURCES) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.xml,.wsdl,.yaml,.yml,.graphql,.gql,application/json,application/xml,text/xml,application/x-yaml,text/yaml';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (file) {
        const fileName = file.name.toLowerCase();
        const allowedExtensions = ['.json', '.xml', '.yaml', '.yml', '.graphql', '.gql', '.wsdl'];
        const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
        
        if (isValidExtension) {
          const newId = Date.now();
          // Add the file entry immediately with file but no fileName yet (will show uploading); respect max total
          setAdditionalFiles(prev => (prev.length >= MAX_ADDITIONAL_SOURCES ? prev : [...prev, { id: newId, file, s3FileKey: null, fileName: null }]));
          // Set uploading state
          setUploadingFiles(prev => ({ ...prev, [newId]: true }));
          // Trigger upload
          handleUpdateAdditionalFile(newId, file, () => {});
        } else {
          // Show error for invalid file type
          alert('Only JSON, XML/WSDL, YAML, and GraphQL (.graphql/.gql) files are allowed');
        }
      }
    };
    input.click();
  };

  const handleRemoveFile = (id) => {
    setAdditionalFiles(additionalFiles.filter(item => item.id !== id));
    setUploadingFiles(prev => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });
  };

  const handleUpdateAdditionalFile = async (id, file, fieldOnChange) => {
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const allowedExtensions = ['.json', '.xml', '.yaml', '.yml', '.graphql', '.gql', '.wsdl'];
    const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (isValidExtension) {
      setUploadingFiles(prev => ({ ...prev, [id]: true }));
      try {
        const uploadedKey = await uploadFile({
          file: file,
          category: 'tmp',
          type: file.type || 'application/json',
        });
        
        setAdditionalFiles(prev => prev.map(item => 
          item.id === id ? { ...item, file, s3FileKey: uploadedKey, fileName: file.name } : item
        ));
        fieldOnChange(file);
      } catch (error) {
        setServerError(error?.response?.data?.message || error?.message || 'File upload failed');
      } finally {
        setUploadingFiles(prev => {
          const newState = { ...prev };
          delete newState[id];
          return newState;
        });
      }
    }
  };

  const handleSave = async (data) => {
    setIsSaving(true);
    setServerError(null);
    setSchemaEndpointUrlError(null);
    const nonce = msg?.metadata?.nonce || null;

    try {
      // Check if we have files (main file or additional files)
      const hasMainFile = s3FileKey !== null;
      const hasAdditionalFiles = additionalFiles.length > 0 && additionalFiles.some(item => item.s3FileKey);
      const hasFiles = hasMainFile || hasAdditionalFiles;

      // Check if we have URLs (main URL or additional URLs)
      const hasMainUrl = data.collection_url && data.collection_url.trim() !== '';
      const hasAdditionalUrls = additionalUrls.length > 0 && additionalUrls.some(item => item.url && item.url.trim() !== '');
      const hasUrls = hasMainUrl || hasAdditionalUrls;

      // Build payload based on whether we have files or URLs
      const payload = {
        ...(chatId && { chat_id: chatId }),
        ...(nonce && { nonce }),
      };

      if (hasFiles) {
        // If files are uploaded, send files as array of objects with key and name paired
        const files = [];

        // Add main file if exists
        if (hasMainFile && s3FileKey) {
          files.push({
            file_key: s3FileKey,
            ...(uploadedFileName && { file_name: uploadedFileName })
          });
        }

        // Add additional files
        additionalFiles.forEach(item => {
          if (item.s3FileKey) {
            files.push({
              file_key: item.s3FileKey,
              ...(item.fileName && { file_name: item.fileName })
            });
          }
        });

        // Send files array (even for single file)
        payload.files = files;
        /** Server merges steps; clear URL modality so a prior collection URL cannot survive after URL→file switch. */
        payload.api_collection_urls = [];

        // Schema endpoint: path is required only when the uploaded file is GraphQL (not when URL was GraphQL)
        const pathRequiredForGraphQL = fileFormat === 'GraphQL';
        const pathErr = getSchemaEndpointPathError(schemaEndpointPath, pathRequiredForGraphQL);
        if (pathErr) {
          setSchemaEndpointUrlError(pathErr);
          setServerError(pathErr);
          setIsSaving(false);
          return;
        }

        // Only attempt to build schema endpoint URL when a path is provided
        if (schemaEndpointPath?.trim()) {
          const origin = getSchemaEndpointBaseOrigin();
          if (!origin) {
            setSchemaEndpointUrlError('Target URL is required to build Schema Endpoint URL');
            setServerError('Target URL is required to build Schema Endpoint URL');
            setIsSaving(false);
            return;
          }
          const fullSchemaUrl = buildSchemaEndpointFullUrl();
          if (fullSchemaUrl) payload.schema_endpoint_url = fullSchemaUrl;
        }

        /**
         * Final JSON payload structure when files are uploaded:
         * {
         *   "files": [
         *     { "file_key": "s3_key_1", "file_name": "file1.json" },
         *     { "file_key": "s3_key_2", "file_name": "file2.json" }
         *   ],
         *   "schema_endpoint_url": "https://example.com/graphql", // optional
         *   "chat_id": "chat_123", // optional
         *   "nonce": "nonce_value" // optional
         * }
         */
      } else if (hasUrls) {
        // If URLs are provided, send URLs as array
        const urls = [];

        // Add main URL if exists
        if (hasMainUrl) {
          urls.push(data.collection_url.trim());
        }

        // Add additional URLs
        additionalUrls.forEach(item => {
          if (item.url && item.url.trim() !== '') {
            urls.push(item.url.trim());
          }
        });

        // Always send as array (even for single URL)
        payload.api_collection_urls = urls;
        /** Clear file modality so a prior upload cannot survive after file→URL switch. */
        payload.files = [];

        // Schema endpoint: path is required only when the collection URL is GraphQL (not when file was GraphQL)
        const pathRequiredForGraphQL = urlFormat === 'GraphQL';
        const pathErr = getSchemaEndpointPathError(schemaEndpointPath, pathRequiredForGraphQL);
        if (pathErr) {
          setSchemaEndpointUrlError(pathErr);
          setServerError(pathErr);
          setIsSaving(false);
          return;
        }

        // Only attempt to build schema endpoint URL when a path is provided
        if (schemaEndpointPath?.trim()) {
          const origin = getSchemaEndpointBaseOrigin();
          if (!origin) {
            setSchemaEndpointUrlError('Target URL is required to build Schema Endpoint URL');
            setServerError('Target URL is required to build Schema Endpoint URL');
            setIsSaving(false);
            return;
          }
          const fullSchemaUrl = buildSchemaEndpointFullUrl();
          if (fullSchemaUrl) payload.schema_endpoint_url = fullSchemaUrl;
        }

        /**
         * Final JSON payload structure when URLs are provided:
         * {
         *   "api_collection_urls": ["https://api1.com", "https://api2.com", ...],
         *   "schema_endpoint_url": "https://example.com/graphql", // optional
         *   "chat_id": "chat_123", // optional
         *   "nonce": "nonce_value" // optional
         * }
         */
      }

      if (
        embeddedInScheduleModal &&
        typeof onScheduleCollectionComplete === 'function'
      ) {
        const apiConfig = {};
        if (Array.isArray(payload.files) && payload.files.length > 0) {
          apiConfig.api_files = payload.files.map((f) => ({
            file_key: f.file_key,
            ...(f.file_name && { file_name: f.file_name }),
            ...(f.fileName && { file_name: f.fileName }),
          }));
          apiConfig.api_collection_urls = [];
        } else if (Array.isArray(payload.api_collection_urls) && payload.api_collection_urls.length > 0) {
          apiConfig.api_collection_urls = payload.api_collection_urls;
          apiConfig.api_files = [];
        }
        if (payload.schema_endpoint_url) {
          apiConfig.schema_endpoint_url = payload.schema_endpoint_url;
        }
        const schemaPathTrim = schemaEndpointPath?.trim?.() || '';
        if (schemaPathTrim) {
          apiConfig.schema_endpoint_path = schemaPathTrim;
        }
        try {
          await Promise.resolve(onScheduleCollectionComplete(apiConfig));
          setSubmittedData({
            ...payload,
            ...(Array.isArray(payload.files) && payload.files.length > 0 && { api_files: payload.files }),
            fileName: uploadedFileName || null,
          });
        } catch {
          /* parent may reject; keep form open */
        }
        setIsSaving(false);
        return;
      }

      const result = await genPTTestSteps.mutateAsync(payload);

      if (result && result.success === false) {
        setServerError(isHostMismatchError(result) ? HOST_MISMATCH_DISPLAY_MESSAGE : (result?.message || 'Verification failed'));
        setIsSaving(false);
        return;
      }

      setSubmittedData({
        ...payload,
        ...(Array.isArray(payload.files) && payload.files.length > 0 && { api_files: payload.files }),
        fileName: uploadedFileName || null,
      });
      setFormVisible(false);

    } catch (error) {
      const errData = error?.response?.data;
      const errorMessage = isHostMismatchError(errData)
        ? HOST_MISMATCH_DISPLAY_MESSAGE
        : (errData?.message || error?.message || 'Failed to save collection source. Please try again.');
      setServerError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={
        embeddedInScheduleModal
          ? 'w-full'
          : 'w-full md:w-[95%] border border-[#EFEFEF] rounded-xl p-4 bg-white shadow-md transition-all duration-300'
      }
    >
      {formVisible ? (
        <>
          {!suppressSectionChrome && (
            <>
              <div className="font-semibold text-gray-800 mb-4">API Collection Source</div>
              <Divider variant={'middle'} className={'!m-0'} />
            </>
          )}

          <div className={`my-4 text-sm text-gray-600 ${suppressSectionChrome ? '!mt-0' : ''}`}>
            <span className={`font-semibold`} style={{ color: '#FE7754' }}>Please Note:</span> For GraphQL or SOAP collections, ensure that the domain in your collection URL matches your configured user domain. Host mismatch errors occur when domains don't align.
          </div>

          <form onSubmit={handleSubmit(handleSave)} className="space-y-4 w-full md:w-[90%] mt-4">
            <div>
              <div className='flex items-center gap-2'>
                <img src="/images/genpt/link.svg" className='h-6 w-6' />
                <span className='font-semibold text-gray-800'>API Collection URL</span>
              </div>
              <div className='text-gray-500 text-sm mt-2'>Enter the API collection URL. Accepts GraphQL, SOAP/WSDL, JSON, or YAML formats.</div>
              <Controller
                name="collection_url"
                control={control}
                rules={{
                  // Don't use website_url pattern here: field can be empty when user chooses file, and pattern fails on '' causing red border on blur when clicking upload
                  validate: (value) => {
                    if (!value || value.trim() === '') return true;
                    try {
                      new URL(value);
                      return true;
                    } catch {
                      return 'Please enter a valid URL';
                    }
                  }
                }}
                render={({ field }) => (
                  <div className="flex items-start gap-2 w-full">
                    <div className="flex-1 min-w-0">
                      <MuiTextField
                        {...field}
                        label="API Collection URL"
                        placeholder="eg: https://api.example.com/v1/schema"
                        error={!s3FileKey && !isUploading && Boolean(errors.collection_url)}
                        helperText={!s3FileKey && !isUploading ? errors.collection_url?.message : undefined}
                        disabled={!!s3FileKey}
                        onChange={(e) => {
                          field.onChange(e);
                          if (e.target.value.trim() !== '') {
                            setValue('collection_file', '', { shouldValidate: false });
                            setS3FileKey(null);
                            setUploadedFileName(null);
                            setIsPrefilledFromSession(false);
                            setIsUrlPrefilledFromSession(false); // User manually changed URL
                            setIsUrlVerified(false); // Reset verification when URL changes
                            setUrlFormat(null); // Reset provider
                            setAdditionalUrls([]); // Clear additional URLs
                            setSchemaEndpointPath(''); setSchemaEndpointUrlError(null); // Clear schema endpoint URL
                          } else {
                            setIsUrlVerified(false); // Reset verification when URL is cleared
                            setUrlFormat(null); // Reset provider
                            setAdditionalUrls([]); // Clear additional URLs
                            setSchemaEndpointPath(''); setSchemaEndpointUrlError(null); // Clear schema endpoint URL
                            setError('collection_url', null); // Clear URL field error so red border goes away when user clears prefilled URL
                            setServerError(null);
                          }
                        }}
                      />
                    </div>
                    <div className="flex-shrink-0 min-w-[100px] flex justify-end pt-6">
                      {isUrlVerified ? (
                        <div className="px-4 py-2 border-2 rounded font-semibold text-sm whitespace-nowrap flex items-center gap-1.5" style={{ borderColor: '#3ADA7A', color: '#3ADA7A' }}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                          Verified
                        </div>
                      ) : (
                        <Button
                          type="button"
                          onClick={handleVerifyUrl}
                          disabled={!collectionUrl?.trim() || !!s3FileKey || isVerifyingUrl || urlHasError}
                          className="!bg-primary-500 hover:!bg-primary-600"
                          size="small"
                        >
                          {isVerifyingUrl ? (
                            <AutorenewIcon className="animate-spin text-white" style={{ fontSize: '20px' }} />
                          ) : (
                            'Verify'
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              />
              {isUrlVerified && urlFormat === 'GraphQL' && (
                <>
                  {additionalUrls.map((item) => (
                    <div key={item.id} className="mt-2 flex items-start gap-2 w-full">
                      <div className="flex-1 min-w-0">
                        <MuiTextField
                          label="API Collection URL"
                          placeholder="Enter the API Collection URL"
                          value={item.url}
                          onChange={(e) => handleUpdateAdditionalUrl(item.id, 'url', e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div className="flex-shrink-0 min-w-[100px] flex justify-end pt-6">
                        <button
                          type="button"
                          onClick={() => handleRemoveUrl(item.id)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          title="Remove"
                        >
                          <CloseIcon style={{ fontSize: '20px' }} />
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="mt-3 w-full">
                    <Button
                      type="button"
                      onClick={handleAddMoreUrl}
                      disabled={additionalUrls.length >= MAX_ADDITIONAL_SOURCES}
                      variant="outlined"
                      size="small"
                      className="w-full !border-primary-500 !text-primary-500 hover:!bg-primary-50 disabled:!opacity-50 disabled:!cursor-not-allowed"
                    >
                      + Add Additional Source
                    </Button>
                    <div className="text-gray-500 text-sm mt-2">
                      {additionalUrls.length >= MAX_ADDITIONAL_SOURCES
                        ? `Maximum ${MAX_ADDITIONAL_SOURCES + 1} sources (1 main + ${MAX_ADDITIONAL_SOURCES} additional).`
                        : 'Add additional sources if your GraphQL definition is split across multiple files.'}
                    </div>
                  </div>
                  <div className="mt-5">
                    <div className='flex items-center gap-2 mb-1'>
                      <img src="/images/genpt/schema_endpoint.svg" className='h-6 w-6' />
                      <span className='font-semibold text-gray-800'>Schema Endpoint URL</span>
                    </div>
                    <div className="text-gray-500 text-sm mb-2">Target URL (base) and path next to it. Required for introspection and schema validation.</div>
                    <div className="flex gap-3 items-stretch">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-600 mb-1">Target URL (read-only)</div>
                        <div className="min-h-[40px] flex items-center px-3 py-2 bg-gray-50 border border-gray-200 rounded text-gray-800 text-sm font-mono break-all">
                          {getSchemaEndpointBaseOrigin() || '—'}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0 flex items-center">
                        <MuiTextField
                          label="Path (no slash — e.g. graphql, api) *"
                          placeholder="e.g. graphql or api"
                          value={schemaEndpointPath}
                          onChange={handleSchemaEndpointPathChange}
                          error={Boolean(schemaEndpointUrlError)}
                          helperText={schemaEndpointUrlError}
                          className="w-full"
                          size="small"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center my-4">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="px-4 text-gray-500 text-sm">OR</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>

            <div>
<div className='flex items-center gap-2'>
            <img src="/images/genpt/upload.svg" className='h-6 w-6' />
            <span className='font-semibold text-gray-800'>API Collection File</span>
            </div>
            <div className='text-gray-500 text-sm my-2'>API collection file (JSON, YAML, GraphQL SDL, or SOAP/WSDL)</div>
            <Controller
              name="collection_file"
              control={control}
              rules={{
                validate: (value) => {
                  if (!value) return true;
                  
                  const file = value instanceof File ? value : null;
                  if (!file) return true;
                  
                  const fileName = file.name.toLowerCase();
                  const allowedExtensions = ['.json', '.xml', '.yaml', '.yml', '.graphql', '.gql', '.wsdl'];
                  const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
                  
                  if (!isValidExtension) {
                    return 'Only JSON, XML/WSDL, YAML, and GraphQL (.graphql/.gql) files are allowed';
                  }
                  
                  return true;
                }
              }}
              render={({ field }) => (
                <div>
                  <div className="flex items-start gap-2 mb-2 w-full">
                    <div className="flex-1 min-w-0">
                      <div
                    onDragOver={(e) => {
                      if (collectionUrl && collectionUrl.trim() !== '') return;
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={(e) => {
                      if (collectionUrl && collectionUrl.trim() !== '') return;
                      e.preventDefault();
                      setIsDragging(false);
                    }}
                    onDrop={(e) => {
                      if (collectionUrl && collectionUrl.trim() !== '') return;
                      e.preventDefault();
                      setIsDragging(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) {
                        handleFileSelect(file, field.onChange);
                      }
                    }}
                    className={`
                      border-2 border-dashed rounded-lg p-4 transition-colors
                      ${collectionFile && collectionFile instanceof File || shouldShowUploadedFile ? 'text-left' : 'text-center'}
                      ${collectionUrl && collectionUrl.trim() !== '' 
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60' 
                        : collectionFile && collectionFile instanceof File || shouldShowUploadedFile
                          ? '' 
                          : 'cursor-pointer hover:border-primary-400 hover:bg-primary-50'}
                      ${isDragging ? 'border-primary-500 bg-primary-50' : 'border-gray-300'}
                      ${errors.collection_file ? 'border-red-500' : ''}
                    `}
                    onClick={() => {
                      if (collectionUrl && collectionUrl.trim() !== '') return;
                      if (!collectionFile || !(collectionFile instanceof File)) {
                        document.getElementById('collection-file-input')?.click();
                      }
                    }}
                  >
                    <input
                      id="collection-file-input"
                      type="file"
                      accept=".json,.xml,.wsdl,.yaml,.yml,.graphql,.gql,application/json,application/xml,text/xml,application/x-yaml,text/yaml"
                      className="hidden"
                      disabled={!!(collectionUrl && collectionUrl.trim() !== '')}
                      onChange={(e) => {
                        if (collectionUrl && collectionUrl.trim() !== '') return;
                        const file = e.target.files?.[0];
                        handleFileSelect(file, field.onChange);
                      }}
                    />
                    {isUploading ? (
                      <div className="flex flex-col items-center gap-2">
                        <AutorenewIcon className="animate-spin text-primary-500 h-8 w-8" />
                        <div className="text-gray-600 text-sm">Uploading file...</div>
                      </div>
                    ) : collectionFile && collectionFile instanceof File ? (
                      <div className="flex items-center justify-between w-full gap-2 flex-nowrap">
                        <div className="flex-1 flex items-center gap-3 bg-gray-50 p-2 rounded-lg min-w-0">
                          <div className="flex-shrink-0 h-10 px-2.5 flex items-center justify-center gap-1.5" style={{ backgroundColor: '#B3ECEC', borderRadius: '6px' }}>
                            <img src="/images/genpt/file-code.svg" alt="" className="w-3.5 h-3.5" />
                            <span className="text-xs font-semibold leading-none" style={{ color: '#128688' }}>
                              {getFileExtension(collectionFile.name)}
                            </span>
                          </div>
                          <div className="flex flex-col min-w-0 flex-1">
                            <div className="font-semibold text-gray-800 truncate" title={collectionFile.name}>{collectionFile.name}</div>
                            <div className="text-gray-500 text-sm">{formatFileSize(collectionFile.size)}</div>
                          </div>
                        </div>
                        {!(collectionUrl && collectionUrl.trim() !== '') && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setS3FileKey(null);
                              setUploadedFileName(null);
                              setIsPrefilledFromSession(false);
                              setIsFileVerified(false); // Reset verification when file is removed
                              setFileFormat(null); // Reset provider
                              setAdditionalFiles([]); // Clear additional files
                              setSchemaEndpointPath(''); setSchemaEndpointUrlError(null); // Clear schema endpoint URL
                              setServerError(null); // Clear verification error when file is removed
                              setError('collection_file', null); // Clear field error when file is removed
                              clearErrors('collection_url'); // Clear URL field error so it is not red when field is shown again after removing file
                              field.onChange(null);
                              const input = document.getElementById('collection-file-input');
                              if (input) input.value = '';
                            }}
                            className="text-primary-500 font-semibold underline text-sm hover:text-red-500 transition-colors duration-200"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ) : shouldShowUploadedFile ? (
                      <div className="flex items-center justify-between w-full gap-2 flex-nowrap">
                        <div className="flex-1 flex items-center gap-3 bg-gray-50 p-2 rounded-lg min-w-0">
                          <div className="flex-shrink-0 h-10 px-2.5 flex items-center justify-center gap-1.5" style={{ backgroundColor: '#B3ECEC', borderRadius: '6px' }}>
                            <img src="/images/genpt/file-code.svg" alt="" className="w-3.5 h-3.5" />
                            <span className="text-xs font-semibold leading-none" style={{ color: '#128688' }}>
                              {getFileExtension(uploadedFileName)}
                            </span>
                          </div>
                          <div className="flex flex-col min-w-0 flex-1">
                            <div className="font-semibold text-gray-800 truncate" title={uploadedFileName}>{uploadedFileName}</div>
                          </div>
                        </div>
                        {!(collectionUrl && collectionUrl.trim() !== '') && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setS3FileKey(null);
                              setUploadedFileName(null);
                              setIsPrefilledFromSession(false);
                              setIsFileVerified(false); // Reset verification when file is removed
                              setFileFormat(null); // Reset provider
                              setAdditionalFiles([]); // Clear additional files
                              setSchemaEndpointPath(''); setSchemaEndpointUrlError(null); // Clear schema endpoint URL
                              setServerError(null); // Clear verification error when file is removed
                              setError('collection_file', null); // Clear field error when file is removed
                              clearErrors('collection_url'); // Clear URL field error so it is not red when field is shown again after removing file
                              field.onChange(null);
                              const input = document.getElementById('collection-file-input');
                              if (input) input.value = '';
                            }}
                            className="text-primary-500 font-semibold underline text-sm hover:text-red-500 transition-colors duration-200"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <img src="/images/genpt/upload.svg" className="h-8 w-8 text-gray-400" />
                        <div className="text-gray-600 text-sm">Click to upload or drag and drop</div>
                      </div>
                    )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 min-w-[100px] flex justify-end pt-6">
                      {isFileVerified ? (
                        <div className="px-4 py-2 border-2 rounded font-semibold text-sm whitespace-nowrap flex items-center gap-1.5" style={{ borderColor: '#3ADA7A', color: '#3ADA7A' }}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                          Verified
                        </div>
                      ) : (
                        <Button
                          type="button"
                          onClick={handleVerifyFile}
                          disabled={!s3FileKey || isVerifyingFile || isFileVerified || (collectionUrl && collectionUrl.trim() !== '')}
                          className="!bg-primary-500 hover:!bg-primary-600 whitespace-nowrap"
                          size="small"
                        >
                          {isVerifyingFile ? (
                            <AutorenewIcon className="animate-spin text-white" style={{ fontSize: '20px' }} />
                          ) : (
                            'Verify'
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                  {errors.collection_file && (
                    <div className="text-red-500 text-sm mt-1">{errors.collection_file.message}</div>
                  )}
                </div>
              )}
            />
            {isFileVerified && fileFormat === 'GraphQL' && (
              <>
                {additionalFiles.map((item) => {
                  const isUploading = uploadingFiles[item.id];
                  return (
                    <div key={item.id} className="mt-2 flex items-start gap-2 w-full">
                      <div className="flex-1 min-w-0">
                        <div
                          onDragOver={(e) => {
                            if (isUploading) return;
                            e.preventDefault();
                            setIsDragging(true);
                          }}
                          onDragLeave={(e) => {
                            e.preventDefault();
                            setIsDragging(false);
                          }}
                          onDrop={(e) => {
                            if (isUploading) return;
                            e.preventDefault();
                            setIsDragging(false);
                            const file = e.dataTransfer.files?.[0];
                            if (file) {
                              handleUpdateAdditionalFile(item.id, file, () => {});
                            }
                          }}
                          className={`
                            border-2 border-dashed rounded-lg p-4 transition-colors
                            ${item.fileName ? 'text-left' : 'text-center'}
                            ${isUploading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-primary-400 hover:bg-primary-50'}
                            ${isDragging && !isUploading ? 'border-primary-500 bg-primary-50' : 'border-gray-300'}
                          `}
                          onClick={() => {
                            if (isUploading) return;
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = '.json,.xml,.yaml,.yml,application/json,application/xml,text/xml,application/x-yaml,text/yaml';
                            input.onchange = (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleUpdateAdditionalFile(item.id, file, () => {});
                              }
                            };
                            input.click();
                          }}
                        >
                          {isUploading ? (
                            <div className="flex flex-col items-center gap-2">
                              <AutorenewIcon className="animate-spin text-primary-500 h-8 w-8" />
                              <div className="text-gray-600 text-sm">Uploading file...</div>
                            </div>
                          ) : item.fileName ? (
                            <div className="flex items-center justify-between w-full gap-2 flex-nowrap">
                              <div className="flex-1 flex items-center gap-3 bg-gray-50 p-2 rounded-lg min-w-0">
                                <div className="flex-shrink-0 h-10 px-2.5 flex items-center justify-center gap-1.5" style={{ backgroundColor: '#B3ECEC', borderRadius: '6px' }}>
                                  <img src="/images/genpt/file-code.svg" alt="" className="w-3.5 h-3.5" />
                                  <span className="text-xs font-semibold leading-none" style={{ color: '#128688' }}>
                                    {getFileExtension(item.fileName)}
                                  </span>
                                </div>
                                <div className="flex flex-col min-w-0 flex-1">
                                  <div className="font-semibold text-gray-800 truncate" title={item.fileName}>{item.fileName}</div>
                                  {item.file && item.file.size && (
                                    <div className="text-gray-500 text-sm">{formatFileSize(item.file.size)}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-2">
                              <img src="/images/genpt/upload.svg" className="h-8 w-8 text-gray-400" />
                              <div className="text-gray-600 text-sm">Click to upload or drag and drop</div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0 min-w-[100px] flex justify-end pt-6">
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(item.id)}
                          disabled={isUploading}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Remove"
                        >
                          <CloseIcon style={{ fontSize: '20px' }} />
                        </button>
                      </div>
                    </div>
                  );
                })}
                <div className="mt-3 w-full">
                  <Button
                    type="button"
                    onClick={handleAddMoreFile}
                    disabled={additionalFiles.length >= MAX_ADDITIONAL_SOURCES}
                    variant="outlined"
                    size="small"
                    className="w-full !border-primary-500 !text-primary-500 hover:!bg-primary-50 disabled:!opacity-50 disabled:!cursor-not-allowed"
                  >
                    + Add Additional Source
                  </Button>
                  <div className="text-gray-500 text-sm mt-2">
                    {additionalFiles.length >= MAX_ADDITIONAL_SOURCES
                      ? `Maximum ${MAX_ADDITIONAL_SOURCES + 1} sources (1 main + ${MAX_ADDITIONAL_SOURCES} additional).`
                      : 'Add additional sources if your GraphQL definition is split across multiple files.'}
                  </div>
                </div>
                <div className="mt-5">
                  <div className='flex items-center gap-2 mb-1'>
                    <img src="/images/genpt/schema_endpoint.svg" className='h-6 w-6' />
                    <span className='font-semibold text-gray-800'>Schema Endpoint URL</span>
                  </div>
                  <div className="text-gray-500 text-sm mb-2">Target URL (base) and path next to it. Required for introspection and schema validation.</div>
                  <div className="flex gap-3 items-stretch">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-600 mb-1">Target URL (read-only)</div>
                      <div className="min-h-[40px] flex items-center px-3 py-2 bg-gray-50 border border-gray-200 rounded text-gray-800 text-sm font-mono break-all">
                        {getSchemaEndpointBaseOrigin() || '—'}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 flex items-center">
                      <MuiTextField
                        label="Path (no slash — e.g. graphql, api) *"
                        placeholder="e.g. graphql or api"
                        value={schemaEndpointPath}
                        onChange={handleSchemaEndpointPathChange}
                        error={Boolean(schemaEndpointUrlError)}
                        helperText={schemaEndpointUrlError}
                        className="w-full"
                        size="small"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            </div>

            {shouldShowPrefilledNote && (
              <div className="mt-3">
                <div className="text-sm flex items-start gap-2">
                  <span className="">
                    <strong className='text-[#ED6C02]'>Note:</strong> This is your previously configured Collection. Please verify if it is current, or upload the latest version before proceeding.
                  </span>
                </div>
              </div>
            )}

            {serverError && (
              serverError === HOST_MISMATCH_DISPLAY_MESSAGE ? (
                <div
                  role="alert"
                  className="mt-1 flex items-center gap-2 rounded-lg px-3 py-2"
                  style={{
                    background: 'rgba(254, 242, 242, 0.95)',
                    border: '1px solid #DC2626',
                    color: '#202020',
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1.4,
                  }}
                >
                  <WarningAmberIcon
                    fontSize="small"
                    sx={{ color: '#EAB308', flexShrink: 0 }}
                  />
                  <span>
                    <strong>Host mismatch:</strong> The domain in your collection URL does not match the domain entered. Please check and try again.
                  </span>
                </div>
              ) : (
                <ErrorBanner
                  className="mt-1"
                  message={<span>{serverError}</span>}
                />
              )
            )}

            <Divider variant={'middle'} className={'!my-4'} />

            <div className="flex">
              <Button
                type="submit"
                disabled={isSubmitDisabled}
                className={`${
                  isSubmitDisabled
                    ? '!bg-gray-300 cursor-not-allowed'
                    : '!bg-primary-500 hover:!bg-primary-600'
                } flex items-center gap-2`}
              >
                {isSaving ? (
                  <AutorenewIcon className="animate-spin text-white" />
                ) : embeddedInScheduleModal && scheduleModalPrimaryCtaLabel ? (
                  scheduleModalPrimaryCtaLabel
                ) : (
                  'Confirm'
                )}
              </Button>
            </div>
          </form>
        </>
      ) : (
        <>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <GreenCheck />
              <div>
                <Typography variant="subtitle1" className="font-semibold text-gray-800">
                  Collection Source Saved
                </Typography>
              </div>
            </div>
            <button
              onClick={() => setExpanded((prev) => !prev)}
              className="p-2 hover:bg-gray-100 rounded-full transition-all"
            >
              {expanded ? (
                <ExpandLessIcon className="text-gray-600" />
              ) : (
                <ExpandMoreIcon className="text-gray-600" />
              )}
            </button>
          </div>

          {expanded && (
            <div className="mt-4 border-t pt-4 space-y-4 animate-fadeIn">
              {/* URLs */}
              {displayData?.api_collection_urls?.length > 0 && (
                <div className="space-y-3">
                  {displayData.api_collection_urls.map((url, idx) => (
                    <MuiTextField
                      key={idx}
                      label={idx === 0 ? 'API Collection URL' : `Additional URL ${idx}`}
                      value={url}
                      disabled
                    />
                  ))}
                </div>
              )}

              {/* Files */}
              {(displayData?.files?.length > 0 || displayData?.api_files?.length > 0) && (() => {
                const filesList = displayData.api_files || displayData.files;
                return (
                <div className="space-y-3">
                  <div className="text-sm font-semibold text-gray-700">
                    {filesList.length > 1 ? 'API Collection Files' : 'API Collection File'}
                  </div>
                  {filesList.map((file, idx) => {
                    const name = file.file_name || file.fileName || file.file_key?.split('/').pop() || 'Uploaded file';
                    return (
                      <div key={idx} className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg border border-gray-200 min-w-0">
                        <div className="h-10 px-2.5 flex items-center justify-center gap-1.5 flex-shrink-0" style={{ backgroundColor: '#B3ECEC', borderRadius: '6px' }}>
                          <img src="/images/genpt/file-code.svg" alt="" className="w-3.5 h-3.5" />
                          <span className="text-xs font-semibold leading-none" style={{ color: '#128688' }}>
                            {getFileExtension(name)}
                          </span>
                        </div>
                        <div className="flex flex-col flex-1 min-w-0">
                          <div className="font-semibold text-gray-800 truncate" title={name}>{name}</div>
                          <div className="text-gray-500 text-sm">File uploaded successfully</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                );
              })()}

              {/* Schema Endpoint URL */}
              {displayData?.schema_endpoint_url && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <img src="/images/genpt/schema_endpoint.svg" className="h-5 w-5" alt="" />
                    <span className="text-sm font-semibold text-gray-700">Schema Endpoint URL</span>
                  </div>
                  <div className="flex items-center px-3 py-2 bg-gray-50 border border-gray-200 rounded text-gray-800 text-sm font-mono break-all min-h-[40px]">
                    {displayData.schema_endpoint_url}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

